import React, { useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Button, CrmShell, PageHeader } from '../../components/CrmShell';
import { EmptyState, formatCompactMoney, formatDate, formatMoney } from '../../components/CrmForms';
import { Activity, AppUser, Company, Contact, Deal } from '../../types';
import { subscribeToActivities, subscribeToCompanies, subscribeToContacts, subscribeToDeals, subscribeToPipelineSettings } from '../../services/crm';
import { fetchUsers } from '../../services/users';
import { logGlobalError } from '../../utils/globalErrorLogger';
import { buildContactsCsv } from './csv';

export { buildContactsCsv } from './csv';

type PipelineChartRow = {
  name: string;
  value: number;
};

export type ContactReportSortDirection = 'asc' | 'desc';
type ReportTab = 'pipeline' | 'activity';

export function ReportsPage() {
  const [deals, setDeals] = useState<Deal[]>([]);
  const [stages, setStages] = useState<string[]>([]);
  const [users, setUsers] = useState<AppUser[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [contactSortDirection, setContactSortDirection] = useState<ContactReportSortDirection>('desc');
  const [activeTab, setActiveTab] = useState<ReportTab>('pipeline');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const unsubs = [
      subscribeToDeals(setDeals, (err) => setError(err.message)),
      subscribeToCompanies(setCompanies, (err) => setError(err.message)),
      subscribeToContacts(setContacts, (err) => setError(err.message)),
      subscribeToActivities(setActivities, (err) => setError(err.message)),
      subscribeToPipelineSettings((settings) => setStages(settings.stages), (err) => setError(err.message)),
    ];
    fetchUsers().then(setUsers).catch((err) => {
      setError(err.message);
      logGlobalError(err, { context: 'ReportsPage.fetchUsers' });
    });
    return () => unsubs.forEach((unsub) => unsub());
  }, []);

  const openDeals = useMemo(() => deals.filter((deal) => !['Closed Won', 'Closed Lost'].includes(deal.stage)), [deals]);
  const stageData = stages
    .map((stage) => ({
      name: stage,
      value: deals.filter((deal) => deal.stage === stage).reduce((sum, deal) => sum + deal.value, 0),
    }));
  const ownerData = users
    .map((user) => ({
      name: user.displayName || user.email || 'Unknown',
      value: openDeals.filter((deal) => deal.ownerId === user.uid).length,
    }))
    .filter((row) => row.value > 0)
    .sort((a, b) => b.value - a.value);
  const usersById = useMemo(() => new Map(users.map((user) => [user.uid, user])), [users]);
  const companiesById = useMemo(() => new Map(companies.map((company) => [company.id, company])), [companies]);
  const sortedContacts = useMemo(
    () => sortContactsByLastActivity(contacts, contactSortDirection),
    [contactSortDirection, contacts],
  );
  const latestActivitiesByContact = useMemo(() => getLatestActivitiesByContact(activities), [activities]);
  const pipelineByOwnerData = useMemo(() => buildPipelineChartData(
    openDeals,
    (deal) => {
      if (!deal.ownerId) return 'Unassigned';
      const owner = usersById.get(deal.ownerId);
      return owner?.displayName || owner?.email || 'Unknown';
    },
  ), [openDeals, usersById]);
  const pipelineByCompanyData = useMemo(() => buildPipelineChartData(
    openDeals,
    (deal) => {
      if (!deal.companyId) return 'No company';
      return companiesById.get(deal.companyId)?.name || 'Unknown company';
    },
  ), [companiesById, openDeals]);
  const totalOpen = openDeals.reduce((sum, deal) => sum + deal.value, 0);
  const month = new Date().getMonth();
  const year = new Date().getFullYear();
  const closedWon = deals.filter((deal) => deal.stage === 'Closed Won' && deal.updatedAt.getMonth() === month && deal.updatedAt.getFullYear() === year).length;
  const closedLost = deals.filter((deal) => deal.stage === 'Closed Lost' && deal.updatedAt.getMonth() === month && deal.updatedAt.getFullYear() === year).length;

  return (
    <CrmShell>
      <PageHeader
        title="Reports"
        description="Pipeline and contact activity reporting for the team."
      />
      {error && <div className="mb-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      <ReportTabs activeTab={activeTab} onChange={setActiveTab} />
      {activeTab === 'pipeline' ? (
        <div role="tabpanel" id="pipeline-report-panel" aria-labelledby="pipeline-report-tab" tabIndex={0}>
          <div className="grid gap-4 md:grid-cols-4">
            <Metric label="Total open pipeline" value={formatMoney(totalOpen)} />
            <Metric label="Open deals" value={String(openDeals.length)} />
            <Metric label="Closed won this month" value={String(closedWon)} />
            <Metric label="Closed lost this month" value={String(closedLost)} />
          </div>

          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            <HorizontalBarChart
              title="Pipeline value by stage"
              data={stageData}
              emptyMessage="No pipeline value yet."
              valueFormatter={formatMoney}
              tickFormatter={formatCompactMoney}
              barSize={30}
              tickFontSize={11}
            />

            <HorizontalBarChart
              title="Pipeline value by owner"
              data={pipelineByOwnerData}
              emptyMessage="No assigned open pipeline value yet."
              valueFormatter={formatMoney}
              tickFormatter={formatCompactMoney}
            />

            <HorizontalBarChart
              title="Pipeline value by company"
              data={pipelineByCompanyData}
              emptyMessage="No company pipeline value yet."
              valueFormatter={formatMoney}
              tickFormatter={formatCompactMoney}
            />

            <HorizontalBarChart
              title="Deals by owner"
              data={ownerData}
              emptyMessage="No assigned open deals yet."
              valueFormatter={(value) => String(value)}
              allowDecimals={false}
            />
          </div>
        </div>
      ) : (
        <div role="tabpanel" id="activity-report-panel" aria-labelledby="activity-report-tab" tabIndex={0}>
          <ContactReportTable
            contacts={sortedContacts}
            companiesById={companiesById}
            sortDirection={contactSortDirection}
            onSort={() => setContactSortDirection((current) => (current === 'desc' ? 'asc' : 'desc'))}
            onExport={() => downloadContactsCsv(sortedContacts, companiesById, latestActivitiesByContact)}
          />
        </div>
      )}
    </CrmShell>
  );
}

function ReportTabs({ activeTab, onChange }: { activeTab: ReportTab; onChange: (tab: ReportTab) => void }) {
  return (
    <div className="mb-6 border-b border-gray-200" role="tablist" aria-label="Report types">
      <div className="flex gap-6">
        <ReportTabButton
          id="pipeline-report-tab"
          label="Pipeline"
          active={activeTab === 'pipeline'}
          onClick={() => onChange('pipeline')}
        />
        <ReportTabButton
          id="activity-report-tab"
          label="Activity"
          active={activeTab === 'activity'}
          onClick={() => onChange('activity')}
        />
      </div>
    </div>
  );
}

function ReportTabButton({
  id,
  label,
  active,
  onClick,
}: {
  id: string;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      id={id}
      role="tab"
      aria-selected={active}
      aria-controls={id.replace('-tab', '-panel')}
      onClick={onClick}
      className={`border-b-2 px-1 pb-3 text-sm font-medium ${
        active ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
      }`}
    >
      {label}
    </button>
  );
}

export function sortContactsByLastActivity(
  contacts: Contact[],
  direction: ContactReportSortDirection,
): Contact[] {
  return [...contacts].sort((a, b) => {
    const aTime = validTimestamp(a.lastActivityAt);
    const bTime = validTimestamp(b.lastActivityAt);

    // Contacts without an activity stay at the bottom in either sort direction.
    if (aTime === null && bTime !== null) return 1;
    if (aTime !== null && bTime === null) return -1;
    if (aTime !== null && bTime !== null && aTime !== bTime) {
      return direction === 'asc' ? aTime - bTime : bTime - aTime;
    }

    return contactName(a).localeCompare(contactName(b));
  });
}

function downloadContactsCsv(
  contacts: Contact[],
  companiesById: ReadonlyMap<string, Company>,
  latestActivitiesByContact: ReadonlyMap<string, Activity>,
): void {
  const blob = new Blob([`\uFEFF${buildContactsCsv(contacts, companiesById, latestActivitiesByContact)}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `contacts-report-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function validTimestamp(value: Date | null): number | null {
  if (!value) return null;
  const timestamp = value.getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
}

function contactName(contact: Contact): string {
  return [contact.firstName, contact.lastName].filter(Boolean).join(' ').trim() || contact.email || 'Unnamed contact';
}

function getCompanyName(contact: Contact, companiesById: ReadonlyMap<string, Company>): string {
  return (contact.companyId ? companiesById.get(contact.companyId)?.name : null) || contact.companyName || '';
}

function getLatestActivitiesByContact(activities: Activity[]): Map<string, Activity> {
  const latest = new Map<string, Activity>();
  activities.forEach((activity) => {
    const current = latest.get(activity.contactId);
    const activityTime = validTimestamp(activity.activityDate);
    const currentTime = current ? validTimestamp(current.activityDate) : null;
    if (!current || (activityTime !== null && (currentTime === null || activityTime > currentTime))) {
      latest.set(activity.contactId, activity);
    }
  });
  return latest;
}

function ContactReportTable({
  contacts,
  companiesById,
  sortDirection,
  onSort,
  onExport,
}: {
  contacts: Contact[];
  companiesById: ReadonlyMap<string, Company>;
  sortDirection: ContactReportSortDirection;
  onSort: () => void;
  onExport: () => void;
}) {
  return (
    <section className="mt-6 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="flex flex-col gap-3 border-b border-gray-200 px-5 py-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Contacts</h2>
          <p className="mt-1 text-sm text-gray-500">All contacts with their company and most recent activity.</p>
        </div>
        <Button variant="secondary" onClick={onExport}>
          <Download className="mr-1 inline h-4 w-4" />Export contact activity CSV
        </Button>
      </div>
      {contacts.length ? (
        <div className="overflow-x-auto">
          <table className="min-w-[640px] w-full text-sm">
            <thead className="bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2">Contact</th>
                <th className="px-3 py-2">Company</th>
                <th
                  className="px-3 py-2"
                  aria-sort={sortDirection === 'desc' ? 'descending' : 'ascending'}
                >
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 font-medium uppercase tracking-wide hover:text-gray-900"
                    onClick={onSort}
                    aria-label={`Sort by last activity date ${sortDirection === 'desc' ? 'ascending' : 'descending'}`}
                    title={`Sort ${sortDirection === 'desc' ? 'oldest first' : 'newest first'}`}
                  >
                    Last activity date
                    {sortDirection === 'desc' ? <ArrowDown className="h-3.5 w-3.5" /> : <ArrowUp className="h-3.5 w-3.5" />}
                  </button>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {contacts.map((contact) => {
                const companyName = getCompanyName(contact, companiesById);
                return (
                  <tr key={contact.id}>
                    <td className="px-3 py-2">
                      <Link className="font-medium text-primary hover:underline" to={`/contacts/${contact.id}`}>
                        {contactName(contact)}
                      </Link>
                      {contact.email && <div className="text-xs text-gray-500">{contact.email}</div>}
                    </td>
                    <td className="px-3 py-2 text-gray-700">
                      {contact.companyId && companiesById.has(contact.companyId) ? (
                        <Link className="text-primary hover:underline" to={`/companies/${contact.companyId}`}>
                          {companyName}
                        </Link>
                      ) : companyName || '—'}
                    </td>
                    <td className="px-3 py-2 text-gray-500">{formatDate(contact.lastActivityAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : <EmptyState>No contacts yet.</EmptyState>}
    </section>
  );
}

function buildPipelineChartData(deals: Deal[], getName: (deal: Deal) => string): PipelineChartRow[] {
  const totals = new Map<string, number>();
  deals.forEach((deal) => {
    const name = getName(deal);
    totals.set(name, (totals.get(name) ?? 0) + deal.value);
  });

  return Array.from(totals, ([name, value]) => ({ name, value }))
    .filter((row) => row.value > 0)
    .sort((a, b) => b.value - a.value);
}

function HorizontalBarChart({
  title,
  data,
  emptyMessage,
  valueFormatter,
  tickFormatter = valueFormatter,
  allowDecimals = true,
  barSize,
  tickFontSize = 12,
}: {
  title: string;
  data: PipelineChartRow[];
  emptyMessage: string;
  valueFormatter: (value: number) => string;
  tickFormatter?: (value: number) => string;
  allowDecimals?: boolean;
  barSize?: number;
  tickFontSize?: number;
}) {
  const axisWidth = Math.min(132, Math.max(72, Math.max(...data.map((row) => row.name.length), 0) * 7));
  const hasValue = data.some((row) => row.value > 0);

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
      <div className="mt-4 h-80">
        {hasValue ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} layout="vertical" margin={{ left: 4, right: 12 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                type="number"
                allowDecimals={allowDecimals}
                tick={{ fontSize: tickFontSize }}
                tickFormatter={(value) => tickFormatter(Number(value))}
              />
              <YAxis
                dataKey="name"
                type="category"
                width={axisWidth}
                tick={{ fontSize: tickFontSize }}
                tickFormatter={(value) => truncateAxisLabel(String(value))}
              />
              <Tooltip formatter={(value) => valueFormatter(Number(value))} />
              <Bar dataKey="value" fill="#25286a" radius={[0, 4, 4, 0]} barSize={barSize} />
            </BarChart>
          </ResponsiveContainer>
        ) : <EmptyState>{emptyMessage}</EmptyState>}
      </div>
    </section>
  );
}

function truncateAxisLabel(value: string): string {
  return value.length > 18 ? `${value.slice(0, 17)}...` : value;
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-2 text-2xl font-bold text-gray-900">{value}</div>
    </section>
  );
}
